import {useEffect, useMemo, useState, type ReactNode} from "react";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {
    ArrowsPointingInIcon,
    ArrowsPointingOutIcon,
    CheckIcon,
    Cog6ToothIcon,
    DocumentTextIcon,
    FunnelIcon,
} from "@heroicons/react/24/outline";
import WideDrawer from "../../../general/WideDrawer.tsx";
import {cn} from "../../../../lib/utils.ts";
import type {Money} from "../../../../lib/Money.ts";
import type {GlAccount} from "../../GLASelector/index.ts";
import type {DocumentSettlement, DrillDownLine} from "../types/types.ts";
import {
    BookingGroupBox,
    BookingRow,
    BookingTableHeader,
    DRILL_DOWN_MIN_WIDTH,
    DrillDownColgroup,
    FilterChips,
    JournalEntryFold,
    PanelHeader,
    PanelTaskMessage,
    BookToAccountModal,
    SelectionFooter,
} from "./components/index.ts";
import {isOpenItemsAccount, showsPaymentStatus} from "./constants.ts";
import {BookingFilter, BookingSortColumn, type BookingSort, type PanelBooking} from "./types.ts";
import {useDrillDownKeyboard} from "./useDrillDownKeyboard.ts";
import {
    DEFAULT_SORT,
    accountNature,
    applyFilter,
    axisLabel,
    buildAxisGroupIndex,
    dropPrePeriodBookings,
    filterCounts,
    groupVisibleBookings,
    groupingAxisFor,
    matchesSearch,
    openGroups,
    panelTotals,
    restrictSelection,
    selectedSum,
    sortBookings,
    toPanelBookings,
    toggleSelectAll,
    toggleSelection,
    toggleSort,
    visibleFilterChips,
    type BookingIndex,
} from "./utils/index.ts";
import type { CurrencyEnum } from '../../../../generated/enums.ts';
import {InvoiceVatView} from "./components/InvoiceVatView/InvoiceVatView.tsx";

const GROUPED_VIEW_STORAGE_KEY = "drilldown.groupedView";
const ONLY_OPEN_STORAGE_KEY = "drilldown.showOnlyOpen";
const VAT_VIEW_STORAGE_KEY = "drilldown.vatView";
const GROUP_BY_RELATION_STORAGE_KEY = "drilldown.groupByRelation";

function readStoredBool(key: string): boolean | null {
    try {
        const value = localStorage.getItem(key);
        return value === null ? null : value === "true";
    } catch {
        return null;
    }
}

function writeStoredBool(key: string, value: boolean) {
    try {
        localStorage.setItem(key, String(value));
    } catch {
        return;
    }
}

export interface AccountDrillDownPanelProps {
    open: boolean;
    onClose: () => void;
    accountCode: string;
    accountLabel: string;
    subledgerSplit: boolean;
    periodStartDate: string;
    periodEndDate: string;
    currency: CurrencyEnum;
    lines: DrillDownLine[];
    documentSettlements: DocumentSettlement[];
    bookingIndex: BookingIndex;
    openingBalance: Money | null;
    signedNet: Money | null;
    glAccounts: GlAccount[];
    canGroupBookings: boolean;
    canSeeVatViews: boolean;
    onOpenDocument: (documentId: string) => void;
    onOpenTransaction: (transactionId: string) => void;
    onOpenManualBooking?: (manualBookingId: string) => void;
    onBookToAccount: (bookings: PanelBooking[], target: GlAccount) => void;
    taskMessage: string | null;
    onDismissTaskMessage: () => void;
}

export function AccountDrillDownPanel({
    open,
    onClose,
    accountCode,
    accountLabel,
    subledgerSplit,
    periodStartDate,
    periodEndDate,
    currency,
    lines,
    documentSettlements,
    bookingIndex,
    openingBalance,
    signedNet,
    glAccounts,
    canGroupBookings,
    canSeeVatViews,
    onOpenDocument,
    onOpenTransaction,
    onOpenManualBooking,
    onBookToAccount,
    taskMessage,
    onDismissTaskMessage,
}: AccountDrillDownPanelProps) {
    const [searchQuery, setSearchQuery] = useState("");
    const [filter, setFilter] = useState<BookingFilter>(BookingFilter.All);
    const [sort, setSort] = useState<BookingSort>(DEFAULT_SORT);
    const [showOnlyOpen, setShowOnlyOpen] = useState(() => readStoredBool(ONLY_OPEN_STORAGE_KEY) ?? false);
    const [groupedView, setGroupedView] = useState(
        () => readStoredBool(GROUPED_VIEW_STORAGE_KEY) ?? isOpenItemsAccount(accountCode),
    );
    const [vatView, setVatView] = useState(() => readStoredBool(VAT_VIEW_STORAGE_KEY) ?? false);
    const [groupByRelation, setGroupByRelation] = useState(
        () => readStoredBool(GROUP_BY_RELATION_STORAGE_KEY) ?? false,
    );
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [anchorKey, setAnchorKey] = useState<string | null>(null);
    const [modalBookings, setModalBookings] = useState<PanelBooking[] | null>(null);

    const axis = groupingAxisFor(accountCode);

    useEffect(() => {
        setSearchQuery("");
        setFilter(BookingFilter.All);
        setSort(DEFAULT_SORT);
        setModalBookings(null);
    }, [open, accountCode]);

    useEffect(() => {
        writeStoredBool(GROUPED_VIEW_STORAGE_KEY, groupedView);
    }, [groupedView]);

    useEffect(() => {
        writeStoredBool(ONLY_OPEN_STORAGE_KEY, showOnlyOpen);
    }, [showOnlyOpen]);

    useEffect(() => {
        writeStoredBool(VAT_VIEW_STORAGE_KEY, vatView);
    }, [vatView]);

    useEffect(() => {
        writeStoredBool(GROUP_BY_RELATION_STORAGE_KEY, groupByRelation);
    }, [groupByRelation]);

    useEffect(() => {
        setExpandedGroups(new Set());
        setExpandedKeys(new Set());
        setSelectedKeys(new Set());
        setAnchorKey(null);
    }, [open, accountCode, lines]);

    const bookings = useMemo(
        () =>
            dropPrePeriodBookings(
                toPanelBookings(lines, bookingIndex, accountCode),
                openingBalance,
                periodStartDate,
            ),
        [lines, bookingIndex, accountCode, openingBalance, periodStartDate],
    );

    const nature = useMemo(() => accountNature(signedNet, bookings), [signedNet, bookings]);
    const totals = useMemo(() => panelTotals(bookings, currency), [bookings, currency]);

    const searchedBookings = useMemo(
        () => bookings.filter((booking) => matchesSearch(booking, searchQuery)),
        [bookings, searchQuery],
    );
    const counts = useMemo(() => filterCounts(searchedBookings, nature), [searchedBookings, nature]);

    useEffect(() => {
        if (filter !== BookingFilter.All && !visibleFilterChips(counts, nature).some((chip) => chip.filter === filter)) {
            setFilter(BookingFilter.All);
        }
    }, [filter, counts, nature]);

    const visibleBookings = useMemo(
        () => sortBookings(applyFilter(searchedBookings, filter, nature), sort),
        [searchedBookings, filter, nature, sort],
    );

    const axisIndex = useMemo(
        () => buildAxisGroupIndex(bookings, axis, documentSettlements, currency),
        [bookings, axis, documentSettlements, currency],
    );
    const groups = useMemo(
        () => groupVisibleBookings(visibleBookings, axisIndex, currency),
        [visibleBookings, axisIndex, currency],
    );
    const visibleGroups = useMemo(
        () => (showOnlyOpen ? openGroups(groups) : groups),
        [showOnlyOpen, groups],
    );

    const useVatView = canGroupBookings && canSeeVatViews && vatView;
    const useGroupedView = canGroupBookings && groupedView && !useVatView;
    const groupKeys = useMemo(() => visibleGroups.map((group) => group.key), [visibleGroups]);

    function toggleGroup(key: string) {
        setExpandedGroups((previous) => {
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    const orderedSelectableKeys = useMemo(
        () => visibleBookings.filter((booking) => booking.selectable).map((booking) => booking.key),
        [visibleBookings],
    );

    useEffect(() => {
        setSelectedKeys((current) => {
            const next = restrictSelection(current, orderedSelectableKeys);
            if (next.size === current.size && [...next].every((key) => current.has(key))) return current;
            return next;
        });
        setAnchorKey((current) => (current !== null && orderedSelectableKeys.includes(current) ? current : null));
    }, [orderedSelectableKeys]);

    const selectedBookings = useMemo(
        () => visibleBookings.filter((booking) => selectedKeys.has(booking.key)),
        [visibleBookings, selectedKeys],
    );
    const selectedTotal = useMemo(
        () => selectedSum(visibleBookings, selectedKeys, currency),
        [visibleBookings, selectedKeys, currency],
    );

    const allVisibleSelected =
        orderedSelectableKeys.length > 0 &&
        orderedSelectableKeys.every((key) => selectedKeys.has(key));
    const someVisibleSelected = orderedSelectableKeys.some((key) => selectedKeys.has(key));

    const showOpeningBalanceEmpty = bookings.length === 0 && openingBalance !== null;
    const showBareEmpty = bookings.length === 0 && openingBalance === null;

    function handleSortChange(column: BookingSortColumn) {
        setSort((current) => toggleSort(current, column));
        setExpandedKeys(new Set());
    }

    function handleToggleExpand(key: string) {
        setExpandedKeys((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    function handleCollapse(key: string) {
        setExpandedKeys((current) => {
            if (!current.has(key)) return current;
            const next = new Set(current);
            next.delete(key);
            return next;
        });
    }

    function handleToggleSelect(key: string, shiftKey: boolean) {
        const extendsRange = shiftKey && !useVatView;
        const change = toggleSelection(orderedSelectableKeys, selectedKeys, key, extendsRange, anchorKey);
        setSelectedKeys(change.selected);
        setAnchorKey(change.anchorKey);
    }

    function handleToggleSelectAll() {
        setSelectedKeys(toggleSelectAll(orderedSelectableKeys, selectedKeys));
        setAnchorKey(null);
    }

    const keyboard = useDrillDownKeyboard({
        active: open && !useVatView,
        bookings: visibleBookings,
        expandedKeys,
        onToggleExpand: handleToggleExpand,
        onCollapse: handleCollapse,
        onToggleSelect: (key) => handleToggleSelect(key, false),
        onExtendSelectionTo: (key) => handleToggleSelect(key, true),
        onToggleSelectAll: handleToggleSelectAll,
    });

    function handleChangeAccountForBooking(booking: PanelBooking) {
        setModalBookings([booking]);
    }

    function handleBookToAccountConfirm(target: GlAccount) {
        const bookingsToMove = modalBookings ?? [];
        setModalBookings(null);
        onBookToAccount(bookingsToMove, target);
    }

    function renderBookingRow(booking: PanelBooking) {
        return (
            <BookingRowGroup
                key={booking.key}
                booking={booking}
                subledgerSplit={subledgerSplit}
                selected={selectedKeys.has(booking.key)}
                expanded={expandedKeys.has(booking.key)}
                onToggleExpand={() => handleToggleExpand(booking.key)}
                onToggleSelect={(shiftKey) => handleToggleSelect(booking.key, shiftKey)}
                tabIndex={keyboard.rowTabIndex(booking.key)}
                onFocus={() => keyboard.focusRow(booking.key)}
                onOpenDocument={onOpenDocument}
                onOpenManualBooking={onOpenManualBooking ?? null}
                onOpenTransaction={onOpenTransaction}
                onChangeAccount={() => handleChangeAccountForBooking(booking)}
            />
        );
    }

    return (
        <WideDrawer
            open={open}
            updateOpenState={onClose}
            title={
                <span>
                    Bronnen · <span className="text-primary">{accountCode}</span> · {accountLabel}
                </span>
            }
            width="xl"
            overflowClassName="overflow-hidden"
            contentClassName="min-h-0"
        >
            <div className="flex h-full flex-col">
                <PanelTaskMessage message={taskMessage} onDismiss={onDismissTaskMessage} />
                {showBareEmpty ? (
                    <EmptySources />
                ) : (
                    <>
                        <PanelHeader
                            bookingCount={bookings.length}
                            periodStartDate={periodStartDate}
                            periodEndDate={periodEndDate}
                            totals={totals}
                            openingBalance={openingBalance}
                        />
                        {showOpeningBalanceEmpty ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">
                                Geen boekingen in deze periode. De beginbalans staat hierboven.
                            </p>
                        ) : (
                            <>
                                <FilterChips
                                    activeFilter={filter}
                                    counts={counts}
                                    nature={nature}
                                    searchQuery={searchQuery}
                                    onSearchChange={setSearchQuery}
                                    onFilterChange={setFilter}
                                    leading={
                                        useGroupedView && (
                                            <OpenItemsFilterMenu
                                                onlyOpen={showOnlyOpen}
                                                onOnlyOpenChange={setShowOnlyOpen}
                                            />
                                        )
                                    }
                                    actions={
                                        canGroupBookings && (
                                            <ViewSettingsMenu
                                                grouped={useGroupedView}
                                                groupedDescription={axisLabel(axis)}
                                                onGroupedChange={(grouped) => {
                                                    setVatView(false);
                                                    setGroupedView(grouped);
                                                }}
                                                showVatViews={canSeeVatViews}
                                                vatView={useVatView}
                                                vatViewByRelation={groupByRelation}
                                                onVatViewSelect={(byRelation) => {
                                                    setGroupByRelation(byRelation);
                                                    setVatView(true);
                                                }}
                                                onExpandAll={() =>
                                                    setExpandedGroups(new Set(groupKeys))
                                                }
                                                onCollapseAll={() => setExpandedGroups(new Set())}
                                            />
                                        )
                                    }
                                />
                                <div
                                    ref={keyboard.containerRef}
                                    className={
                                        useGroupedView || useVatView
                                            ? "-mr-4 min-h-0 flex-1 overflow-y-auto pr-4 sm:-mr-6 sm:pr-6"
                                            : "min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200"
                                    }
                                >
                                    {useVatView ? (
                                        <InvoiceVatView
                                            key={accountCode}
                                            groupByRelation={groupByRelation}
                                            selectAll={{
                                                allSelected: allVisibleSelected,
                                                someSelected: someVisibleSelected,
                                                disabled: orderedSelectableKeys.length === 0,
                                                onToggle: handleToggleSelectAll,
                                            }}
                                            bookings={visibleBookings}
                                            accountCode={accountCode}
                                            currency={currency}
                                            documentSettlements={documentSettlements}
                                            renderBookings={(rowBookings) => rowBookings.map(renderBookingRow)}
                                        />
                                    ) : useGroupedView ? (
                                        <>
                                            <table className={cn("sticky top-0 z-20 w-full table-fixed bg-white", DRILL_DOWN_MIN_WIDTH)}>
                                                <DrillDownColgroup />
                                                <BookingTableHeader
                                                    allSelected={allVisibleSelected}
                                                    someSelected={someVisibleSelected}
                                                    selectAllDisabled={orderedSelectableKeys.length === 0}
                                                    onToggleSelectAll={handleToggleSelectAll}
                                                    sort={sort}
                                                    onSortChange={handleSortChange}
                                                />
                                            </table>
                                            {visibleBookings.length === 0 ? (
                                                <EmptyState label="Geen boekingen voor dit filter." />
                                            ) : visibleGroups.length === 0 ? (
                                                <EmptyState label="Niets openstaand." />
                                            ) : (
                                                <div className="flex flex-col gap-4 py-4">
                                                    {visibleGroups.map((group) => (
                                                        <BookingGroupBox
                                                            key={group.key}
                                                            settlement={group.settlement ?? undefined}
                                                            label={group.label}
                                                            net={group.net}
                                                            showStatus={showsPaymentStatus(accountCode)}
                                                            expanded={expandedGroups.has(group.key)}
                                                            onToggle={() => toggleGroup(group.key)}
                                                        >
                                                            {group.bookings.map(renderBookingRow)}
                                                        </BookingGroupBox>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <table role="grid" className={cn("w-full table-fixed border-collapse", DRILL_DOWN_MIN_WIDTH)}>
                                            <DrillDownColgroup />
                                            <BookingTableHeader
                                                allSelected={allVisibleSelected}
                                                someSelected={someVisibleSelected}
                                                selectAllDisabled={orderedSelectableKeys.length === 0}
                                                onToggleSelectAll={handleToggleSelectAll}
                                                sort={sort}
                                                onSortChange={handleSortChange}
                                            />
                                            <tbody>
                                                {visibleBookings.length === 0 ? (
                                                    <EmptyTableRow label="Geen boekingen voor dit filter." />
                                                ) : (
                                                    visibleBookings.map(renderBookingRow)
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <SelectionFooter
                                    selectedCount={selectedBookings.length}
                                    selectedTotal={selectedTotal}
                                    onBookToAccount={() => setModalBookings(selectedBookings)}
                                />
                            </>
                        )}
                    </>
                )}
            </div>

            <BookToAccountModal
                open={modalBookings !== null}
                selectedCount={modalBookings?.length ?? 0}
                accounts={glAccounts}
                currentAccountCode={accountCode}
                onClose={() => setModalBookings(null)}
                onConfirm={handleBookToAccountConfirm}
            />
        </WideDrawer>
    );
}

function EmptyTableRow({label}: {label: string}) {
    return (
        <tr>
            <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">
                {label}
            </td>
        </tr>
    );
}

function EmptyState({label}: {label: string}) {
    return <div className="px-4 py-8 text-center text-sm text-muted-foreground">{label}</div>;
}

interface ViewSettingsMenuProps {
    grouped: boolean;
    groupedDescription: string;
    onGroupedChange: (grouped: boolean) => void;
    showVatViews: boolean;
    vatView: boolean;
    vatViewByRelation: boolean;
    onVatViewSelect: (byRelation: boolean) => void;
    onExpandAll: () => void;
    onCollapseAll: () => void;
}

function ViewSettingsMenu({
    grouped,
    groupedDescription,
    onGroupedChange,
    showVatViews,
    vatView,
    vatViewByRelation,
    onVatViewSelect,
    onExpandAll,
    onCollapseAll,
}: ViewSettingsMenuProps) {
    return (
        <Menu as="div" className="relative">
            <MenuButton
                className="flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                aria-label="Weergave-instellingen"
            >
                <Cog6ToothIcon className="size-5" aria-hidden />
            </MenuButton>
            <MenuItems
                anchor={{to: "bottom end", gap: 8}}
                className="z-50 w-72 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
                <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Weergave
                </p>
                <ViewSettingsOption
                    label="Gegroepeerd"
                    description={groupedDescription}
                    active={grouped}
                    onClick={() => onGroupedChange(true)}
                />
                <ViewSettingsOption
                    label="Platte boekingen"
                    active={!grouped && !vatView}
                    onClick={() => onGroupedChange(false)}
                />
                {showVatViews && (
                    <>
                        <ViewSettingsOption
                            label="Gegroepeerd per btw"
                            description="Per factuur, regels per btw-tarief"
                            active={vatView && !vatViewByRelation}
                            onClick={() => onVatViewSelect(false)}
                        />
                        <ViewSettingsOption
                            label="Per relatie en btw"
                            description="Per relatie, daaronder facturen per btw-tarief"
                            active={vatView && vatViewByRelation}
                            onClick={() => onVatViewSelect(true)}
                        />
                    </>
                )}
                <div className="my-1 border-t border-gray-200" />
                <ViewSettingsAction
                    label="Alles uitklappen"
                    icon={<ArrowsPointingOutIcon className="size-4" aria-hidden />}
                    disabled={!grouped}
                    onClick={onExpandAll}
                />
                <ViewSettingsAction
                    label="Alles inklappen"
                    icon={<ArrowsPointingInIcon className="size-4" aria-hidden />}
                    disabled={!grouped}
                    onClick={onCollapseAll}
                />
            </MenuItems>
        </Menu>
    );
}

function OpenItemsFilterMenu({
    onlyOpen,
    onOnlyOpenChange,
}: {
    onlyOpen: boolean;
    onOnlyOpenChange: (value: boolean) => void;
}) {
    return (
        <Menu as="div" className="relative">
            <MenuButton
                className={cn(
                    "flex size-9 items-center justify-center rounded-md hover:bg-muted",
                    onlyOpen ? "text-primary" : "text-muted-foreground",
                )}
                aria-label="Filter"
            >
                <FunnelIcon className="size-5" aria-hidden />
            </MenuButton>
            <MenuItems
                anchor={{to: "bottom end", gap: 8}}
                className="z-50 w-56 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
                <ViewSettingsOption
                    label="Alleen openstaand"
                    active={onlyOpen}
                    onClick={() => onOnlyOpenChange(!onlyOpen)}
                />
            </MenuItems>
        </Menu>
    );
}

function ViewSettingsOption({
    label,
    description,
    active,
    onClick,
}: {
    label: string;
    description?: string;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <MenuItem>
            <button
                type="button"
                onClick={onClick}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-foreground data-[focus]:bg-muted"
            >
                <span className="flex flex-col">
                    <span>{label}</span>
                    {description && <span className="text-xs text-muted-foreground">{description}</span>}
                </span>
                {active && <CheckIcon className="size-4 shrink-0 text-primary" aria-hidden />}
            </button>
        </MenuItem>
    );
}

function ViewSettingsAction({
    label,
    icon,
    disabled,
    onClick,
}: {
    label: string;
    icon: ReactNode;
    disabled: boolean;
    onClick: () => void;
}) {
    return (
        <MenuItem disabled={disabled}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                className={cn(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground data-[focus]:bg-muted",
                    disabled && "cursor-not-allowed opacity-40",
                )}
            >
                {icon}
                <span>{label}</span>
            </button>
        </MenuItem>
    );
}

function EmptySources() {
    return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <DocumentTextIcon className="h-8 w-8 text-muted-foreground" aria-hidden />
            </div>
            <h3 className="text-sm font-medium text-foreground">Geen bronnen gevonden</h3>
            <p className="mt-1 text-sm text-muted-foreground">
                Er zijn geen boekingen gevonden voor deze rekening in deze periode.
            </p>
        </div>
    );
}

interface BookingRowGroupProps {
    booking: PanelBooking;
    subledgerSplit: boolean;
    selected: boolean;
    expanded: boolean;
    tabIndex: 0 | -1;
    onToggleExpand: () => void;
    onToggleSelect: (shiftKey: boolean) => void;
    onFocus: () => void;
    onOpenDocument: (documentId: string) => void;
    onOpenManualBooking: ((manualBookingId: string) => void) | null;
    onOpenTransaction: (transactionId: string) => void;
    onChangeAccount: () => void;
}

function BookingRowGroup({
    booking,
    subledgerSplit,
    selected,
    expanded,
    tabIndex,
    onToggleExpand,
    onToggleSelect,
    onFocus,
    onOpenDocument,
    onOpenManualBooking,
    onOpenTransaction,
    onChangeAccount,
}: BookingRowGroupProps) {
    return (
        <>
            <BookingRow
                booking={booking}
                subledgerSplit={subledgerSplit}
                selected={selected}
                expanded={expanded}
                tabIndex={tabIndex}
                onToggleExpand={onToggleExpand}
                onToggleSelect={onToggleSelect}
                onFocus={onFocus}
            />
            {expanded && (
                <JournalEntryFold
                    booking={booking}
                    onOpenDocument={onOpenDocument}
                    onOpenManualBooking={onOpenManualBooking}
                    onOpenTransaction={onOpenTransaction}
                    onChangeAccount={booking.selectable ? onChangeAccount : null}
                />
            )}
        </>
    );
}
